// decideProposal — chat's decisions for a proposal's card in the thread
// (pen "Chat – every entry" §6): Accept / Discard each ask their own port;
// once durable the thread re-reads (an accept also tells the loaders the
// material changed); a failure shows a notice and changes nothing. Pure.

import { exhaustSwitchCase } from "@bilda/utils";
import type { ChatCommand } from "./command.js";
import type { ChatDecision } from "./effect.js";

type ProposalCommand = Extract<
    ChatCommand,
    {
        readonly type:
            | "proposal_accept_requested"
            | "proposal_discard_requested"
            | "proposal_accepted"
            | "proposal_discarded"
            | "proposal_settle_failed";
    }
>;

const effectsOnly = (effects: ChatDecision["effects"]): ChatDecision => ({
    events: [],
    effects,
    signals: [],
});

export const decideProposal = (command: ProposalCommand): ChatDecision => {
    switch (command.type) {
        case "proposal_accept_requested":
            return effectsOnly([
                {
                    type: "accept_proposal",
                    conversationId: command.conversationId,
                    proposalId: command.proposalId,
                },
            ]);
        case "proposal_discard_requested":
            return effectsOnly([
                {
                    type: "reject_proposal",
                    conversationId: command.conversationId,
                    proposalId: command.proposalId,
                },
            ]);
        // Accepting changed the material: loaders re-read it too.
        case "proposal_accepted":
            return {
                ...effectsOnly([
                    {
                        type: "reload_transcript",
                        conversationId: command.conversationId,
                    },
                ]),
                signals: [{ _tag: "Signal.WorldChanged" }],
            };
        case "proposal_discarded":
            return effectsOnly([
                {
                    type: "reload_transcript",
                    conversationId: command.conversationId,
                },
            ]);
        case "proposal_settle_failed":
            return {
                events: [],
                effects: [],
                signals: [
                    {
                        _tag: "Signal.NoticeRaised",
                        notice: { type: "edit-failed" },
                    },
                ],
            };
        default:
            return exhaustSwitchCase(command);
    }
};
